import { WorkerWrapper } from '~/workers/workerWrapper'
import workerTimeCodeString from '@/workers/workerTime?worker'
import { type ToastWrapperInstance } from 'vant'
type Callback = (e: Event) => void
type MessageCallback<RT> = (e: RT) => void

interface IOptions<RT> {
  url: string | null // 链接的通道的地址
  debug: boolean // 是否打开日志
  heartTime?: number // 心跳时间间隔
  isRestory?: boolean // 是否销毁
  reconnectTime?: number // 重连时间间隔
  reconnectCount?: number // 重连次数 -1 则不限制
  openCb?: Callback // 连接成功的回调
  closeCb?: Callback // 关闭的回调
  messageCb?: MessageCallback<RT> // 消息的回调
  errorCb?: Callback // 错误的回调
  heartCb?: Callback // 发送心跳回调
  reconnectCb: () => void // 重连的回调
}

// const workerTimeCodeBlob = new Blob([workerTimeCodeString], {
//   type: 'application/javascript'
// }) // worker执行的文件需要用字符串方式引入，要不然会报跨域等问题 The operation is insecure
//#region 心跳
/* 浏览器支持worker，优先用worker，不支持就不用 */
class Heart {
  worker!: WorkerWrapper
  heartTimeOut!: number // 心跳计时器
  ServerHeartTimeOut!: number // 心跳计时器
  timeout = 5000
  reconnectToast: ToastWrapperInstance | null = null // 重连轻提示

  // 创建worker，浏览器不支持就不用worker
  createWorker() {
    if (window.Worker) {
      this.worker = new WorkerWrapper(new workerTimeCodeString())
    }
  }

  // 重置
  heartReset(): void {
    if (window.Worker) {
      // worker模式心跳
      this.worker.clearTimer('heartStart')
    } else {
      // 普通模式
      clearTimeout(this.heartTimeOut)
      clearTimeout(this.ServerHeartTimeOut)
    }
  }

  /**
   * 启动心跳
   * @param {Function} cb 回调函数
   */
  heartStart(cb: Callback): void {
    if (window.Worker) {
      // worker模式心跳
      this.worker
        .postMessage({
          action: 'start',
          id: 'heartStart',
          delay: 5000
        })
        .then((res) => {
          cb && cb(res)
          this.heartStart(cb)
        })
    } else {
      // 普通模式心跳
      this.heartTimeOut = setTimeout((e: Event) => {
        cb(e)
        this.ServerHeartTimeOut = setTimeout((e: Event) => {
          cb(e)
          // 重新开始检测
          this.heartReset()
          this.heartStart(cb)
        }, this.timeout)
      }, this.timeout)
    }
  }
}
//#endregion

//#region Socket
export default class Socket<T, RT> extends Heart {
  ws!: WebSocket
  reconnectTimer: null | ReturnType<typeof setTimeout> = null // 重连计时器
  options: IOptions<RT> = {
    url: null, // 链接的通道的地址
    debug: false, // 是否打开日志
    isRestory: false, // 是否销毁
    reconnectTime: 5000, // 重连时间间隔
    openCb: (e: Event) => {
      console.log('socket => 连接成功的默认回调::::', e)
    }, // 连接成功的回调
    closeCb: (e: Event) => {
      console.log('socket => 关闭的默认回调::::', e)
    }, // 关闭的回调
    messageCb: (e: RT) => {
      console.log('socket => 消息的默认回调::::', e)
    }, // 消息的回调
    errorCb: (e: Event) => {
      console.log('socket => 错误的默认回调::::', e)
    }, // 错误的回调
    heartCb: (e: Event) => {
      console.log('socket => 心跳的默认回调::::', e)
    }, // 重连的回调
    reconnectCb: () => {
      console.log('socket => 重连的默认回调::::')
    }
  }

  //构造函数
  constructor(ops: IOptions<RT>) {
    super()
    Object.assign(this.options, ops)

    document.addEventListener('visibilitychange', this.visibilitychangeCb)

    this.createWorker()
    this.create()
  }

  //

  /**
   * 建立连接
   */
  create(): void {
    if (!('WebSocket' in window)) {
      throw new Error('当前浏览器不支持，无法使用')
    }
    if (!this.options.url) {
      throw new Error('地址不存在，无法建立通道')
    }
    // this.ws = null
    console.log('--------------socket create()', new Date().toISOString())
    console.log('socket => create')
    this.ws = new WebSocket(this.options.url)
    this.onopen(this.options.openCb as Callback)
    this.onclose(this.options.closeCb as Callback)
    this.onmessage(this.options.messageCb as MessageCallback<RT>)
    this.onerror(this.options.errorCb as Callback)
  }

  /**
   * 自定义连接成功事件
   * 如果callback存在，调用callback，不存在调用OPTIONS中的回调
   * @param {Function} callback 回调函数
   */
  onopen(callback: Callback): void {
    this.ws.onopen = (event) => {
      console.log('--------------socket onopen()', new Date().toISOString())
      console.log('socket => onopen')
      this.connectTimeReset() // 重置重连定时器
      // 建立心跳机制
      this.heartStart(() => {
        if (typeof this.options.heartCb === 'function') {
          this.options.heartCb(event)
        }
      })

      if (typeof callback === 'function') {
        callback(event)
      } else {
        typeof this.options.openCb === 'function' && this.options.openCb(event)
      }
    }
  }

  /**
   * 自定义关闭事件
   * 如果callback存在，调用callback，不存在调用OPTIONS中的回调
   * @param {Function} callback 回调函数
   */
  onclose(callback: Callback): void {
    this.ws.onclose = (event) => {
      console.log('socket => onclose')
      this.heartReset()
      !this.options.isRestory && this.onreconnect()
      if (typeof callback === 'function') {
        callback(event)
      } else {
        typeof this.options.closeCb === 'function' &&
          this.options.closeCb(event)
      }
    }
  }

  /**
   * 自定义错误事件
   * 如果callback存在，调用callback，不存在调用OPTIONS中的回调
   * @param {Function} callback 回调函数
   */
  onerror(callback: Callback): void {
    this.ws.onerror = (event) => {
      console.log('socket => onerror')
      if (typeof callback === 'function') {
        callback(event)
      } else {
        typeof this.options.errorCb === 'function' &&
          this.options.errorCb(event)
      }
    }
  }

  /**
   * 自定义消息监听事件
   * 如果callback存在，调用callback，不存在调用OPTIONS中的回调
   * @param {Function} callback 回调函数
   */
  onmessage(callback: MessageCallback<RT>): void {
    this.ws.onmessage = (event: MessageEvent<string>) => {
      const strMessage = event.data
      const res = JSON.parse(strMessage)
      //心跳信息不打印&&如果开启debug就打印
      if (res.route !== 'heartbeat' && this.options.debug) {
        console.log(`socket => onMsg：${res.res_data ? 'res' : 'req'}:`, res)
      }
      // 收到任何消息，重新开始倒计时心跳检测
      if (typeof callback === 'function') {
        callback(res)
      } else {
        typeof this.options.messageCb === 'function' &&
          this.options.messageCb(res)
      }
    }
  }

  /**
   * 自定义发送消息事件
   * @param {String} data 发送的文本
   */
  send(data: T | string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!navigator.onLine) {
        reject('webSocke: The network is currently unavailable.')
        throw new Error('webSocke: The network is currently unavailable.')
      }
      if (this.ws.readyState !== this.ws.OPEN) {
        reject('webSocke: Not connected to server, unable to push.')
        throw new Error('webSocke: Not connected to server, unable to push.')
      }
      data = JSON.stringify(data)
      this.ws.send(data)
      resolve(true)
    })
  }

  /**
   * 重置重连定时器
   */
  connectTimeReset() {
    if (window.Worker) {
      this.worker.clearTimer('reconnectTimer')
    } else {
      if (Number(this.reconnectTimer)) {
        clearTimeout(Number(this.reconnectTimer))
      }
    }
  }

  /**
   * 连接事件
   */
  onreconnect(): void {
    console.log('socket => 开始重连')
    this.reconnectToast = showToast('Reconnecting or logging in, please wait.')
    if (window.Worker) {
      // worker模式
      this.worker
        .postMessage({
          action: 'start',
          id: 'reconnectTimer',
          delay: this.options.reconnectTime
        })
        .then(() => {
          this.create()
          if (typeof this.options.reconnectCb === 'function') {
            this.options.reconnectCb()
          }
        })
    } else {
      // 普通模式
      this.connectTimeReset()
      this.reconnectTimer = setTimeout(() => {
        this.create()
        if (typeof this.options.reconnectCb === 'function') {
          this.options.reconnectCb()
        }
      }, this.options.reconnectTime)
    }
  }

  /**
   * visibilitychange 页面显示创建
   */
  visibilitychangeCb(): void {
    if (!document.hidden && this.ws && this.ws.readyState !== this.ws.OPEN) {
      // 页面显示；有创建过WebSocket；不是连击状态
      // 就直接重连
      this.create()
    }
  }

  /**
   * 销毁
   */
  destroy(): void {
    this.heartReset()
    this.connectTimeReset()

    if (window.Worker) {
      this.worker.terminate()
    }

    document.removeEventListener('visibilitychange', this.visibilitychangeCb)

    this.options.isRestory = true
    this.ws.close()
    console.log('socket => 销毁成功')
  }
}
//#endregion
